import React, { useEffect } from 'react';
import css from './index.module.scss';
import { IFilterState } from '@helpers/types/quotes';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  DEFAULT_INSURANCE_COST,
  getCountryOfOriginOption,
  getMaterialLocationCountryOption,
  toQuoteProductOption
} from '@components/common/quote-wrapper/components/helper';
import { Button } from '@components/base';
import { CURRENCY, OPERATION_TYPE } from '@helpers/constants';
import ConsolidationQuote from '@components/common/quote-wrapper/components/consolidation/components/consolidation';
import CostCalculator from '@components/common/quote-wrapper/components/cost-calculator';
import { yupResolver } from '@hookform/resolvers/yup';
import { consolidationAddEditQuoteValidation } from '@helpers/yup/add-quote.schema';
import { notifyFirstFormError } from '@helpers/utils';

interface IConsolidatedQuoteProps {
  quoteFormState: any;
  setQuoteFormState: any;
  filterState: IFilterState;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  filterPorts: (event?: any, param?: any, fieldType?: string) => Promise<void>;
  onClose: () => void;
  onFormSubmit: (enquiryQuote: any) => Promise<void>;
  data: any;
}

const ConsolidatedQuote = (props: IConsolidatedQuoteProps) => {
  const {
    quoteFormState,
    setQuoteFormState,
    filterState,
    filterProductOptions,
    filterPorts,
    onClose,
    onFormSubmit,
    data
  } = props;

  const {
    selectedWeightSystemChildOptions,
    selectedCurrency,
    productOptions,
    portOfLoadingOptions,
    portOfDischargeOptions,
    isEdit,
    primaryUnit,
    currency1,
    fxRate
  } = quoteFormState;

  const quoteForm = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(consolidationAddEditQuoteValidation),
    defaultValues: {
      quote: [CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });
  const { control, handleSubmit, getValues } = quoteForm;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  useEffect(() => {
    if (data) {
      quoteForm.reset({
        quote: [
          {
            quoteIsExpanded: true,
            product: (data?.products || []).map((item: any) => ({
              productName: toQuoteProductOption(item.product_data),
              productPackaging: item.packaging,
              dealQuantity: item.quantity,
              dealQuantityUnit: item.quantity_unit
                ? selectedWeightSystemChildOptions?.find(
                    (unit: any) => unit.value === item.quantity_unit
                  )
                : primaryUnit,
              base_price: {
                value: item.base_price?.value,
                currency: item.base_price
                  ? CURRENCY?.find((currency) => currency.value === item.base_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.base_price?.cost_id
              },
              final_exWorks_price: {
                value: item.ex_works_price?.value,
                currency: item.ex_works_price
                  ? CURRENCY?.find((currency) => currency.value === item.ex_works_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.ex_works_price?.cost_id
              },
              fob_price: {
                value: item.fob_price?.value,
                currency: item.fob_price
                  ? CURRENCY?.find((currency) => currency.value === item.fob_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.fob_price?.cost_id,
                manualOverride: !!item.fob_price?.value
              },
              cif_price: {
                value: item.cif_price?.value,
                currency: item.cif_price
                  ? CURRENCY?.find((currency) => currency.value === item.cif_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.cif_price?.cost_id,
                manualOverride: !!item.cif_price?.value
              },
              cfr_price: {
                value: item.cfr_price?.value,
                currency: item.cfr_price
                  ? CURRENCY?.find((currency) => currency.value === item.cfr_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.cfr_price?.cost_id,
                manualOverride: !!item.cfr_price?.value
              },
              finalQuote_selling_price: {
                value: item.final_price?.value,
                currency: item.final_price
                  ? CURRENCY?.find((currency) => currency.value === item.final_price?.currency)
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.final_price?.cost_id,
                weightUnit: item?.final_price_weight_unit
                  ? selectedWeightSystemChildOptions?.find(
                      (weight: any) => weight.value === item.final_price_weight_unit
                    )
                  : primaryUnit
              },
              additionalCost: {
                value: item.additional_cost?.value,
                currency: item.additional_cost
                  ? CURRENCY?.find(
                      (currencyItem) => currencyItem.value === item.additional_cost?.currency
                    )
                  : currency1,
                cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : item.additional_cost?.cost_id
              },
              profitMarginExpected: item.profit_margin,
              moq: item.moq,
              moqUnit: item.moq_unit
                ? selectedWeightSystemChildOptions?.find(
                    (items: any) => items.value === item.moq_unit
                  )
                : primaryUnit,
              countryOfOrigin: getCountryOfOriginOption(item.origin, item?.origin_display_value),
              materialLocationCountry: getMaterialLocationCountryOption(
                item?.material_location_country,
                item?.material_location_country_display_value
              ),
              quote_id: item?.quote_id ?? null
            })),
            totalWeight: data.total_weight ?? 0,
            totalWeightUnit: data.total_weight_unit
              ? selectedWeightSystemChildOptions?.find(
                  (item: any) => item.value === data.total_weight_unit
                )
              : primaryUnit,
            totalLocalFreight: {
              value: data.total_local_freight?.value,
              currency: data.total_local_freight
                ? CURRENCY?.find((item) => item.value === data.total_local_freight?.currency)
                : currency1,
              cost_id:
                isEdit === OPERATION_TYPE.DUBLICATE ? null : data.total_local_freight?.cost_id
            },
            totalPortAndClearanceExpenses: {
              value: data.total_port_and_clearance_expenses?.value,
              currency: data.total_port_and_clearance_expenses
                ? CURRENCY?.find(
                    (item) => item.value === data.total_port_and_clearance_expenses?.currency
                  )
                : currency1,
              cost_id:
                isEdit === OPERATION_TYPE.DUBLICATE
                  ? null
                  : data.total_port_and_clearance_expenses?.cost_id
            },
            totalFreight: {
              value: data.total_freight?.value,
              currency: data.total_freight
                ? CURRENCY?.find((item) => item.value === data.total_freight?.currency)
                : currency1,
              cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : data.total_freight?.cost_id
            },
            switchBlCost: {
              value: data.switch_bl_cost?.value,
              currency: data.switch_bl_cost
                ? CURRENCY?.find((item) => item.value === data.switch_bl_cost?.currency)
                : currency1,
              cost_id: isEdit === OPERATION_TYPE.DUBLICATE ? null : data.switch_bl_cost?.cost_id
            },
            portOfLoading: data.pol
              ? {
                  label: data.pol?.name,
                  value: data.pol?.id
                }
              : null,
            portOfDischarge: data.pod
              ? {
                  label: data.pod?.name,
                  value: data.pod?.id
                }
              : null,
            incoTermsLocation: data?.deal_incoterm_location,
            insuranceCost: data.insurance_cost ?? DEFAULT_INSURANCE_COST,
            financeCost: data.finance_cost,
            discount: data.discount,
            incoTerms: data.deal_incoterm
              ? {
                  label: data.deal_incoterm,
                  value: data.deal_incoterm
                }
              : {
                  label: 'CIF',
                  value: 'CIF'
                }
          }
        ]
      });
    }
  }, [data]);

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onFormSubmit, notifyFirstFormError)}
      className={css.mainWrapper}
    >
      <FormProvider {...quoteForm}>
        <CostCalculator
          filters={filterState}
          weightOptions={selectedWeightSystemChildOptions}
          currency={currency1}
          primaryUnitWatch={primaryUnit}
          quote={quoteWatch}
        />
        {fields.map((quote: any, index: number) => {
          return (
            <ConsolidationQuote
              key={index}
              quoteIndex={index}
              product={`quote.${index}.product`}
              quoteIsExpanded={`quote.${index}.quoteIsExpanded`}
              totalWeight={`quote.${index}.totalWeight`}
              totalWeightUnit={`quote.${index}.totalWeightUnit`}
              totalLocalFreight={`quote.${index}.totalLocalFreight.value`}
              totalLocalFreightCurrency={`quote.${index}.totalLocalFreight.currency`}
              totalPortAndClearanceExpenses={`quote.${index}.totalPortAndClearanceExpenses.value`}
              totalPortAndClearanceExpensesCurrency={`quote.${index}.totalPortAndClearanceExpenses.currency`}
              totalFreight={`quote.${index}.totalFreight.value`}
              totalFreightCurrency={`quote.${index}.totalFreight.currency`}
              switchBlCost={`quote.${index}.switchBlCost.value`}
              switchBlCostCurrency={`quote.${index}.switchBlCost.currency`}
              portOfLoading={`quote.${index}.portOfLoading`}
              portOfDischarge={`quote.${index}.portOfDischarge`}
              insuranceCost={`quote.${index}.insuranceCost`}
              discount={`quote.${index}.discount`}
              financeCost={`quote.${index}.financeCost`}
              incoTerms={`quote.${index}.incoTerms`}
              incoTermsLocation={`quote.${index}.incoTermsLocation`}
              filterState={filterState}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
              selectedCurrency={selectedCurrency}
              filterProductOptions={filterProductOptions}
              productOptions={productOptions}
              filterPorts={filterPorts}
              portOfLoadingOptions={portOfLoadingOptions}
              portOfDischargeOptions={portOfDischargeOptions}
              primaryUnitWatch={primaryUnit}
              currency1Watch={currency1}
              fxRateWatch={fxRate}
              fieldLength={fields.length}
            />
          );
        })}
      </FormProvider>
      <section className={css.actionButtons}>
        <Button variant="contained" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="outlined-secondary" type="submit">
          {isEdit == OPERATION_TYPE.EDIT ? 'Update Quote' : 'Create Quote'}
        </Button>
      </section>
    </form>
  );
};

export default ConsolidatedQuote;
